import React, { useState, useEffect } from 'react';
import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Input, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@nimbitlabs/ui';
import { 
  ExternalLink, 
  GitBranch, 
  FileText, 
  Globe, 
  Database, 
  Shield,
  BarChart3,
  MessageSquare
} from 'lucide-react';

export interface ServiceResource {
  id: string;
  name: string;
  url: string;
  type: 'repository' | 'documentation' | 'monitoring' | 'deployment' | 'api' | 'database' | 'security' | 'communication' | 'other';
  description?: string;
}

interface AddEditResourceModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  resource?: ServiceResource | null;
  onSave: (resource: Omit<ServiceResource, 'id'> & { id?: string }) => void;
}

const resourceTypes = [
  { value: 'repository', label: 'Repository', icon: GitBranch },
  { value: 'documentation', label: 'Documentation', icon: FileText },
  { value: 'monitoring', label: 'Monitoring', icon: BarChart3 },
  { value: 'deployment', label: 'Deployment', icon: Globe },
  { value: 'api', label: 'API', icon: ExternalLink },
  { value: 'database', label: 'Database', icon: Database },
  { value: 'security', label: 'Security', icon: Shield },
  { value: 'communication', label: 'Communication', icon: MessageSquare },
  { value: 'other', label: 'Other', icon: Globe }
] as const;

export const AddEditResourceModal: React.FC<AddEditResourceModalProps> = ({ 
  open, 
  onOpenChange, 
  resource,
  onSave 
}) => {
  const [formData, setFormData] = useState({
    name: '',
    url: '',
    type: 'other' as ServiceResource['type'],
    description: ''
  });

  const isEditing = !!resource;

  useEffect(() => {
    if (resource) {
      setFormData({
        name: resource.name,
        url: resource.url,
        type: resource.type,
        description: resource.description || ''
      });
    } else {
      setFormData({
        name: '',
        url: '',
        type: 'other',
        description: ''
      });
    }
  }, [resource, open]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.name && formData.url) {
      onSave({
        id: resource?.id,
        ...formData
      });
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Edit Resource' : 'Add New Resource'}
          </DialogTitle>
          <DialogDescription>
            {isEditing 
              ? 'Update the resource information.' 
              : 'Add a new external integration or resource for this service.'
            }
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g. GitHub Repository"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="type">Type *</Label>
              <Select 
                value={formData.type} 
                onValueChange={(value) => setFormData({ ...formData, type: value as ServiceResource['type'] })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {resourceTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      <div className="flex items-center gap-2">
                        <type.icon className="h-4 w-4" />
                        {type.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="url">URL *</Label>
            <Input
              id="url"
              value={formData.url}
              onChange={(e) => setFormData({ ...formData, url: e.target.value })}
              placeholder="https://..."
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="description">Description (Optional)</Label>
            <Input
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Brief description..."
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit">
              {isEditing ? 'Update Resource' : 'Add Resource'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
